import { useState } from 'react'
import { M2C_HISTORY_PAGE_SIZE, useM2cHistory, type M2cActionResult, type M2cActionState, type M2cDispatchOutcome, type M2cReadEvidence } from '../../api/m2c'
import Card from '../Card'
import SectionHead from '../SectionHead'
import DataTable, { type DataTableColumn } from '../DataTable'
import Pagination from '../Pagination'
import { Badge, type BadgeTone } from '../StatusBadge'

export const secondaryButton = 'rounded-md border border-[var(--border)] bg-[var(--secondary)] px-3 py-2 text-sm text-[var(--secondary-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed disabled:bg-[var(--muted)] disabled:text-[var(--muted-foreground)]'
export const primaryButton = 'rounded-md bg-[var(--primary)] px-3 py-2 text-sm text-[var(--primary-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed disabled:bg-[var(--muted)] disabled:text-[var(--muted-foreground)]'
export const fieldClass = 'w-full rounded-md border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:bg-[var(--muted)]'
export const READ_ONLY_HINT = 'Im Read-only-Modus deaktiviert — Änderungen an Amazon sind gesperrt.'

const euroFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
export const euro = (cents: number) => euroFormat.format(cents / 100)
export const berlinTime = (iso: string) => new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
export const berlinDate = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' })

const STATE_LABELS: Record<M2cActionState, { label: string; tone: BadgeTone }> = {
  proposed: { label: 'Vorgeschlagen', tone: 'neutral' },
  blocked: { label: 'Blockiert', tone: 'bad' },
  cancelled: { label: 'Abgebrochen (keine Änderung)', tone: 'neutral' },
  reserved: { label: 'Reserviert', tone: 'info' },
  dispatching: { label: 'Wird gesendet', tone: 'warn' },
  ack_received: { label: 'Bestätigung erhalten, Readback offen', tone: 'warn' },
  reconcile_required: { label: 'Klärung erforderlich', tone: 'bad' },
  readback_verified: { label: 'Readback bestätigt', tone: 'good' },
  observing: { label: 'In Beobachtung', tone: 'info' },
  evaluation_due: { label: 'Bewertung fällig', tone: 'info' },
  evaluated_success: { label: 'Bewertet: Erfolg', tone: 'good' },
  evaluated_failure: { label: 'Bewertet: Misserfolg', tone: 'bad' },
  insufficient_data: { label: 'Zu wenige Daten', tone: 'neutral' },
  contaminated: { label: 'Verfälscht (externe Änderung)', tone: 'warn' },
  restore_requested: { label: 'Wiederherstellung angefragt', tone: 'info' },
  restoring: { label: 'Wird wiederhergestellt', tone: 'warn' },
  restored: { label: 'Wiederhergestellt', tone: 'good' },
  restore_unresolved: { label: 'Wiederherstellung ungeklärt', tone: 'bad' },
}
export function stateLabel(state: string): string {
  return STATE_LABELS[state as M2cActionState]?.label ?? 'Unbekannter Status'
}
export function StateBadge({ state }: { state: string }) {
  const known = STATE_LABELS[state as M2cActionState]
  return <Badge tone={known?.tone ?? 'neutral'} size="md"><span title={state}>{known?.label ?? 'Unbekannter Status'}</span></Badge>
}
export function outcomeText(outcome: M2cDispatchOutcome | null): string {
  if (!outcome) return '—'
  switch (outcome.status) {
    case 'confirmed': return 'Amazon-Bestätigung (ACK)'
    case 'rejected_pre_dispatch': return `Vor dem Senden abgebrochen: ${outcome.reason}`
    case 'rejected': return `Von Amazon abgelehnt: ${outcome.reason}`
    case 'unknown': return `Unklar: ${outcome.reason}`
    default: return 'Unbekanntes Ergebnis'
  }
}
export function readbackText(evidence: M2cReadEvidence | null): string {
  if (!evidence) return 'Nicht gelesen'
  switch (evidence.status) {
    case 'found': return `${euro(evidence.bidCents)} · ${berlinTime(evidence.observedAt)}`
    case 'not_found': return 'Objekt nicht gefunden'
    case 'identity_mismatch': return `Andere Entity gelesen (${evidence.observedEntity.entityAmazonId})`
    case 'read_error': return `Lesefehler: ${evidence.reason}`
    default: return 'Unbekannter Readback'
  }
}
export const reductionPct = (oldCents: number, targetCents: number) =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format((oldCents - targetCents) * 100 / oldCents) + ' %'

export function M2cActionHistory({ permitId }: { permitId: string }) {
  return <PermitHistory key={permitId} permitId={permitId} />
}

function PermitHistory({ permitId }: { permitId: string }) {
  const [page, setPage] = useState(0)
  const history = useM2cHistory(permitId, page * M2C_HISTORY_PAGE_SIZE)
  const columns: DataTableColumn<M2cActionResult>[] = [
    { key: 'id', label: 'Aktion', render: r => <span className="font-mono text-xs">{r.action.id}</span> },
    { key: 'entity', label: 'Keyword (Amazon-ID)', render: r => <span className="font-mono text-xs" title={`Kampagne ${r.action.entity.campaignAmazonId} · Gruppe ${r.action.entity.adGroupAmazonId}`}>{r.action.entity.entityAmazonId}</span> },
    { key: 'bid', label: 'Gebot alt → Ziel', align: 'right', render: r => <span className="tabular-nums">{`${euro(r.action.oldBidCents)} → ${euro(r.action.targetBidCents)}`}</span> },
    { key: 'state', label: 'Status', render: r => <StateBadge state={r.state} /> },
    { key: 'outcome', label: 'Ergebnis', render: r => outcomeText(r.outcome) },
    { key: 'readback', label: 'Readback', render: r => readbackText(r.readEvidence) },
    { key: 'sent', label: 'Erste Sendung', render: r => r.action.firstDispatchedAt ? berlinTime(r.action.firstDispatchedAt) : '—' },
    { key: 'recovery', label: 'Wiederherstellung bis', render: r => r.action.recoveryExpiresAt ? berlinTime(r.action.recoveryExpiresAt) : '—' },
  ]
  return <Card className="rounded-lg p-4 space-y-3 text-sm" role="region" aria-label="M2c-Aktionsverlauf">
    <SectionHead level={3} title="Aktionsverlauf" subtitle="Server-Journal, auch im Read-only-Modus sichtbar. Eine Amazon-Bestätigung ist technisch und kein Geschäftserfolg." />
    {history.isPending && <div role="status" aria-label="Verlauf wird geladen" className="h-24 animate-pulse rounded-lg bg-[var(--muted)]" />}
    {history.error && <div role="alert" className="space-y-2 text-[var(--destructive)]"><p>{history.error.message}</p>
      <button className={secondaryButton} onClick={() => void history.refetch()}>Erneut versuchen</button></div>}
    {history.data && <>
      <DataTable columns={columns} rows={history.data.actions} getRowId={r => r.action.id} emptyMessage="Noch keine M2c-Aktionen für diesen Permit." />
      <p className="text-xs text-[var(--muted-foreground)]">{new Intl.NumberFormat('de-DE').format(history.data.total)} Aktionen</p>
      {history.data.total > M2C_HISTORY_PAGE_SIZE && <Pagination label="Aktionsverlauf" page={page} pageSize={M2C_HISTORY_PAGE_SIZE} totalItems={history.data.total}
        pageSizeOptions={[M2C_HISTORY_PAGE_SIZE]} onPageChange={setPage} onPageSizeChange={() => {}} />}
    </>}
  </Card>
}

export default M2cActionHistory
